import { merge, noop } from "lodash";

import type { DeepPartial } from "@daangn/idl/rocketchat/api/api";
import {
  type Request,
  type Response,
  Status as RocketchatAPIResponseStatus,
} from "@daangn/idl/rocketchat/api/api";
import { createUniqueId } from "@karrotmarket-com/unique-id";
import type { RocketchatResponseHandler } from "./RocketchatResponseHandler";
import { handleNewMessageEvent } from "./chatEvent/newMessageEventHandler";
import { handleReadMessageEventHandler } from "./chatEvent/readMessageEventHandler";
import { WebSocketReadyState } from "./socket/webSocketStatus";

const REQUEST_TIMEOUT = 5 * 1000;
type CorrelationResponseHandler = {
  resolve: (value: Response) => void;
  reject: (reason?: unknown) => void;
  timeoutId: NodeJS.Timeout;
};
const requestIdToPromiseMap = new Map<string, CorrelationResponseHandler>();

export function sendRocketchatRequestWithPromise({
  socket,
  chatRequest,
}: {
  socket: WebSocket;
  chatRequest: Omit<DeepPartial<Request>, "name"> & { name: string };
}) {
  return new Promise<Response>((resolve, reject) => {
    const requestId = createUniqueId();
    const request = JSON.stringify(
      merge(
        {
          id: requestId,
        },
        chatRequest,
      ),
    );

    if (socket.readyState !== WebSocketReadyState.OPEN) {
      // FIXME: 배포 환경에 따라 에러 메시지 분기
      reject(new Error("Connection is not established. Please try again."));
      return;
    }

    const timeoutId = setTimeout(() => {
      if (requestIdToPromiseMap.has(requestId)) {
        clearTimeout(timeoutId);
        requestIdToPromiseMap.delete(requestId);
        console.error("Request timeout", {
          requestId,
          request,
        });
        reject(new Error("Request timeout"));
      }
    }, REQUEST_TIMEOUT);

    requestIdToPromiseMap.set(requestId, {
      resolve,
      reject,
      timeoutId,
    });

    socket.send(request);
  });
}

const eventResponseHandlerMap: Record<string, RocketchatResponseHandler> = {
  NewMessageEvent: handleNewMessageEvent,
  ReadMessageEvent: handleReadMessageEventHandler,
  /**
   * NOTE 1:1 채팅에서는 상대방이 나갔을때 별도의 UI 변경이 없어 이벤트는 전달받지만 처리하지 않습니다.
   */
  LeaveMemberEvent: noop,
};

export function handleRocketchatResponse({
  messageEvent,
}: {
  messageEvent: MessageEvent;
}) {
  /**
   * NOTE: Response.fromJSON을 사용하지 않고 type assertion을 사용한 이유
   * - API에서 timestamp의 값은 "2025-01-20T16:01:56.000Z" 처럼 문자열로 전달됨.
   * - Response.fromJSON을 거치게 되면 gRPC의 Timestamp type으로 변형됨.
   * - 이때 서버에 pagination 같이 API 응답에 Timestamp를 포함해야하는 경우, 다시 문자열로 변경해야 하는 문제가 생김.
   * - Timestamp.seconds를 다시 문자열로 변경하게 되면 처음 서버에서 받았던 값과 달라지는 문제가 발생.
   */
  const response = JSON.parse(messageEvent.data) as Response;
  const correlationResponseHandler = requestIdToPromiseMap.get(response.id);

  // FIXME: store를 직접 import하지 않고 state, dispatch를 파라미터로 전달받아 사용하도록 변경해야 함
  if (correlationResponseHandler) {
    handleResponseWithRequestId({
      response,
      correlationResponseHandler,
    });
    return;
  }

  const eventHandler = eventResponseHandlerMap[response.name];

  if (!eventHandler) {
    throw new Error(`Not handled response name: ${response.name}`);
  }

  // FIXME: store를 직접 import하지 않고 state, dispatch를 파라미터로 전달받아 사용하도록 변경해야 함
  eventHandler({ response });
}

function handleResponseWithRequestId({
  response,
  correlationResponseHandler,
}: {
  response: Response;
  correlationResponseHandler: CorrelationResponseHandler;
}) {
  const { resolve, reject, timeoutId } = correlationResponseHandler;

  clearTimeout(timeoutId);
  requestIdToPromiseMap.delete(response.id);

  if (response.status === RocketchatAPIResponseStatus.OK) {
    resolve(response);
  } else {
    reject(response);
  }
}
